import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Terminal, Swords, ShieldHalf, Flag, BookOpen, Award,
  Network, Cpu, Zap, CheckCircle2,
} from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/12707786/pexels-photo-12707786.jpeg?auto=compress&cs=tinysrgb&w=1800";
const LABS_BG = "https://images.pexels.com/photos/1181320/pexels-photo-1181320.jpeg?auto=compress&cs=tinysrgb&w=1400";

const CYAN = "text-[#0891b2] dark:text-[#00E5FF]";
const MARQUEE = [
  "RED TEAM", "PROMPT INJECTION", "BLUE TEAM", "RAG POISONING", "JAILBREAKS",
  "SSRF → CLOUD", "MODEL EXTRACTION", "AGENTIC RED TEAM", "SQLI → RCE", "DATA POISONING",
];

const LEVEL_ORDER = ["beginner", "intermediate", "advanced", "developer", "supplychain", "infra", "claudecode101"];
const LEVEL_LABEL = { beginner: "AI Security Analyst", intermediate: "AI Red Teamer", advanced: "AI Red Team Operator", developer: "AI Application Security Engineer", supplychain: "AI Supply Chain Security", infra: "AI Infrastructure Security", claudecode101: "Claude Code 101" };
const LEVEL_NUM = {
  beginner: "text-[#0891b2] dark:text-[#00E5FF]",
  intermediate: "text-amber-500 dark:text-amber-300",
  advanced: "text-[#FF3B30]",
  developer: "text-indigo-500 dark:text-indigo-300",
  supplychain: "text-lime-600 dark:text-lime-300",
  infra: "text-orange-600 dark:text-orange-300",
  claudecode101: "text-sky-600 dark:text-sky-300",
};
const LEVEL_BLURB = {
  beginner: "Foundations: the threat landscape, your first prompt-injection, OWASP LLM Top 10.",
  intermediate: "Go deeper: RAG poisoning, data extraction, jailbreaks, and building real guardrails.",
  advanced: "Break live systems over VPN: agentic red teaming, SSRF-to-cloud, LLM SQLi-to-RCE.",
  developer: "Build & break securely: exploit a vulnerable MCP server with your own Claude Code over VPN.",
  supplychain: "Poison the model itself: malicious-model RCE, token exfil, lateral movement, then defend it.",
  infra: "Attack the infra that runs AI: exploit a real vulnerable AI orchestrator (Langflow CVE) for RCE, then harden it.",
  claudecode101: "New to Claude Code? Learn it hands-on, free: prompt, plan mode, edit real code, fix tests, and commit.",
};

const LOOP = [
  { icon: BookOpen, title: "Read the briefing", body: "Plain-English context, the vocabulary, and the real-world incidents behind each attack." },
  { icon: Terminal, title: "Run the attack", body: "Hands-on against real LLMs in an isolated environment that's yours alone." },
  { icon: Flag, title: "Capture the flag", body: "Prove the exploit worked. Instant feedback tells you the system fell." },
  { icon: ShieldHalf, title: "Defend it", body: "Build the filters and guardrails that make your own exploit start failing." },
];

const FALLBACK = [
  { level: "beginner", count: 8, labs: ["AI Threat Landscape Overview", "Prompt Injection Basics", "OWASP LLM Top 10", "Defensive Monitoring"] },
  { level: "intermediate", count: 8, labs: ["RAG Poisoning", "Sensitive Data Extraction", "Safety Bypass & Jailbreaks", "Building Guardrails"] },
  { level: "advanced", count: 5, labs: ["Agent Host Takeover", "Operation Overreach", "DevOps Copilot — SSRF to Cloud Takeover", "AnalystBot — LLM SQL Injection to RCE"] },
  { level: "developer", count: 1, labs: ["Exploiting a Vulnerable MCP Server (Claude Code)"] },
  { level: "supplychain", count: 1, labs: ["The Model That Loaded a Backdoor"] },
  { level: "infra", count: 1, labs: ["The Orchestrator That Ran Anyone's Code"] },
  { level: "claudecode101", count: 1, labs: ["Claude Code 101 — Your First Agentic Coding Session"] },
];

export default function Landing() {
  const [levels, setLevels] = useState(FALLBACK);
  const [total, setTotal] = useState(21);
  const [freeCount, setFreeCount] = useState(2);
  const [monthlyPrice, setMonthlyPrice] = useState(10);
  const [monthlyCurrency, setMonthlyCurrency] = useState("usd");
  const [monthlyInr, setMonthlyInr] = useState(850);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/curriculum`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.levels?.length) {
          const ordered = LEVEL_ORDER.map((lv) => d.levels.find((x) => x.level === lv)).filter(Boolean);
          setLevels(ordered);
          setTotal(d.total ?? 21);
          setFreeCount((d.free || []).length || 2);
        }
      })
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.monthly?.amount) { setMonthlyPrice(d.monthly.amount); setMonthlyCurrency(d.monthly.currency || "usd"); }
        if (d?.monthly_inr?.amount) { setMonthlyInr(d.monthly_inr.amount); }
      })
      .catch(() => {});
  }, []);

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="bg-zinc-50 dark:bg-[#050505] text-zinc-900 dark:text-white transition-colors" data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex flex-col justify-center px-6 md:px-16 overflow-hidden border-b border-zinc-200 dark:border-white/10">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-0 dark:opacity-60 transition-opacity" />
        <div className="absolute inset-0 bg-zinc-50/70 dark:bg-black/75" />
        <div className="absolute inset-0 tactical-grid opacity-60" />
        <div className="relative max-w-6xl ilfal-fade-up">
          <div data-testid="hero-badge" className="inline-flex items-center gap-2 border border-zinc-300 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-1.5 text-xs font-mono uppercase tracking-[0.2em] text-zinc-600 dark:text-zinc-300 mb-8">
            <span className="w-1.5 h-1.5 bg-[#FF3B30] animate-pulse" /> {total} live labs · new labs every week
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl text-zinc-900 dark:text-white">
            Hack the model.<br />
            <span className="text-[#FF3B30]">Then </span>
            <span className={CYAN}>defend</span> it.
          </h1>
          <p className="mt-8 text-sm sm:text-base font-mono text-zinc-600 dark:text-zinc-300 max-w-2xl leading-relaxed">
            ILFAL is the hands-on way into AI security. Spin up isolated labs with real LLMs, attack live
            systems over VPN or in your browser, then build the guardrails that stop your own exploit — and
            earn certificates that prove it.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register">
              <Button data-testid="hero-cta-register" size="lg"
                className="rounded-none bg-[#FF3B30] text-white font-mono text-sm uppercase tracking-wider px-7 h-12 hover:bg-zinc-900 dark:hover:bg-white dark:hover:text-black active:scale-95 transition-all">
                Start free lab <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#curriculum">
              <Button data-testid="hero-cta-how" size="lg"
                className="rounded-none bg-transparent border border-zinc-300 dark:border-white/20 text-zinc-900 dark:text-white font-mono text-sm uppercase tracking-wider px-7 h-12 hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black active:scale-95 transition-all">
                See the curriculum
              </Button>
            </a>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs font-mono text-zinc-500">
            <span className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${CYAN}`} /> {freeCount} free labs, no card required</span>
            <span className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${CYAN}`} /> Real LLMs, isolated per user</span>
            <span className="flex items-center gap-2"><CheckCircle2 className={`w-4 h-4 ${CYAN}`} /> Bring your own CLI / Burp Suite</span>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-7 border-b border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0A0A0A] overflow-hidden ilfal-marquee-mask">
        <div className="ilfal-marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center" aria-hidden={dup === 1}>
              {MARQUEE.map((t, i) => (
                <span key={`${dup}-${i}`} className="flex items-center text-2xl sm:text-3xl font-display font-bold uppercase tracking-tight text-zinc-200 dark:text-white/20 px-6">
                  {t} <span className="text-[#FF3B30] ml-6">/</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CURRICULUM (live) */}
      <section id="curriculum" className="px-6 md:px-16 py-20 md:py-28 bg-zinc-50 dark:bg-[#050505] border-b border-zinc-200 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-24">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-4">// The curriculum</div>
              <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
                Four tracks.<br />A growing arsenal.
              </h2>
              <p className="mt-6 text-sm font-mono text-zinc-600 dark:text-zinc-400 leading-relaxed">
                {total} labs and counting — we ship new ones <span className="text-zinc-900 dark:text-white">every week</span> as
                attacks, defenses, and models evolve. Your subscription unlocks all of them, plus one certificate per level.
              </p>
              <Link to="/register">
                <Button className="mt-8 rounded-none bg-transparent border border-zinc-300 dark:border-white/20 text-zinc-900 dark:text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black">
                  Browse all labs <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-8 space-y-5" data-testid="landing-curriculum-list">
            {levels.map((c, i) => {
              const samples = (c.labs || []).slice(0, 4);
              const more = (c.count || 0) - samples.length;
              return (
                <div key={c.level} data-testid={`curriculum-${c.level}`}
                  className="group border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0A0A0A] p-6 md:p-8 hover:border-zinc-400 dark:hover:border-white/30 hover:-translate-y-1 transition-all">
                  <div className="flex items-start gap-6">
                    <div className={`font-display font-black text-5xl md:text-6xl leading-none tabular-nums ${LEVEL_NUM[c.level]}`}>
                      {pad(c.count || 0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`text-xs font-mono uppercase tracking-[0.2em] ${LEVEL_NUM[c.level]}`}>
                          {String(i + 1).padStart(2, "0")} / {LEVEL_LABEL[c.level]}
                        </div>
                        <div className="text-xs font-mono text-zinc-500">{c.count} labs</div>
                      </div>
                      <p className="text-sm font-mono text-zinc-600 dark:text-zinc-400 mb-4">{LEVEL_BLURB[c.level]}</p>
                      <div className="flex flex-wrap gap-2">
                        {samples.map((s) => (
                          <span key={s} className="text-xs font-mono text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03] px-2.5 py-1">{s}</span>
                        ))}
                        {more > 0 && <span className="text-xs font-mono text-zinc-500 px-2.5 py-1">+{more} more</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY / VALUE PROP */}
      <section id="why" className="px-6 md:px-16 py-20 md:py-28 bg-white dark:bg-[#0A0A0A] border-b border-zinc-200 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="relative border border-zinc-200 dark:border-white/10 order-2 md:order-1">
            <img src={LABS_BG} alt="Engineer working in a server environment" className="w-full h-[340px] md:h-[460px] object-cover" />
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute -bottom-px -right-px bg-white/95 dark:bg-black/70 backdrop-blur-xl border border-zinc-200 dark:border-white/10 p-5 max-w-[230px]">
              <Network className="w-5 h-5 text-[#FF3B30] mb-2" />
              <div className="font-display font-bold uppercase tracking-tight text-sm text-zinc-900 dark:text-white">Per-user isolated ranges</div>
              <div className="text-xs font-mono text-zinc-600 dark:text-zinc-400 mt-1">Your VPN reaches only your targets. Your flag is randomized per launch.</div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-4">// Why ILFAL</div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
              Real labs.<br />Not simulations.
            </h2>
            <p className="mt-6 text-sm font-mono text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Reading about an attack teaches you the words. Running it — then defending against it — teaches you the
              instinct. Every ILFAL lab is a real, deliberately-vulnerable LLM system you break and then secure.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Swords, t: "Attack, then defend", d: "Paired labs let you exploit a system, then ship the guardrails that stop your own attack." },
                { icon: Cpu, t: "Real LLMs & infrastructure", d: "Ollama-backed agents, Docker ranges, and OpenVPN — driven from your own terminal or Burp." },
                { icon: Zap, t: "Guided when you're stuck", d: "Every lab ships a briefing, key terminology, real incidents, and a full step-by-step solution." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4 border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-[#050505] p-5 hover:border-zinc-400 dark:hover:border-white/30 transition-colors">
                  <f.icon className={`w-5 h-5 ${CYAN} shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-display font-bold uppercase tracking-tight text-sm text-zinc-900 dark:text-white">{f.t}</div>
                    <div className="text-xs font-mono text-zinc-600 dark:text-zinc-400 mt-1 leading-relaxed">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW YOU LEARN */}
      <section className="px-6 md:px-16 py-20 md:py-28 bg-zinc-50 dark:bg-[#050505] border-b border-zinc-200 dark:border-white/10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-4">// The loop</div>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl max-w-3xl leading-[0.95]">
          Every lab follows the same proven loop.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 dark:bg-white/10 border border-zinc-200 dark:border-white/10 mt-12">
          {LOOP.map((s, i) => (
            <div key={i} data-testid={`learn-step-${i}`} className="bg-white dark:bg-[#0A0A0A] p-7 hover:bg-zinc-50 dark:hover:bg-[#101010] transition-colors">
              <div className="flex items-center justify-between mb-5">
                <span className="font-display font-black text-3xl text-zinc-300 dark:text-white/15">{String(i + 1).padStart(2, "0")}</span>
                <s.icon className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <div className="font-display font-bold uppercase tracking-tight text-base text-zinc-900 dark:text-white">{s.title}</div>
              <p className="text-xs font-mono text-zinc-600 dark:text-zinc-400 mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-6 md:px-16 py-20 md:py-28 bg-white dark:bg-[#0A0A0A] border-b border-zinc-200 dark:border-white/10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-4">// Pricing</div>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl">One plan. Every lab.</h2>
          <p className="mt-5 text-sm font-mono text-zinc-600 dark:text-zinc-400">Start with {freeCount} free labs — no credit card. Cancel anytime.</p>
        </div>
        <div className="max-w-md mx-auto" data-testid="landing-pricing-card">
          <div className="bg-zinc-50 dark:bg-[#050505] p-8 border border-zinc-200 dark:border-white/10 relative ring-1 ring-[#FF3B30]" data-testid="pricing-monthly">
            <span className="absolute top-0 right-0 bg-[#FF3B30] text-white text-[10px] font-mono uppercase tracking-wider px-2.5 py-1">All access</span>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30]">Monthly</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display font-black text-6xl" data-testid="pricing-monthly-amount">{monthlyCurrency === "inr" ? `₹${Number(monthlyPrice).toLocaleString("en-IN")}` : `$${monthlyPrice}`}</span>
              <span className="font-mono text-zinc-500">/mo</span>
            </div>
            <p className="mt-2 text-[11px] font-mono text-zinc-500">or ₹{Number(monthlyInr).toLocaleString("en-IN")}/mo in India · UPI + cards</p>
            <p className="mt-3 text-xs font-mono text-zinc-600 dark:text-zinc-400">Auto-renews monthly · cancel anytime.</p>
            <ul className="mt-7 space-y-2.5 text-xs font-mono text-zinc-700 dark:text-zinc-300">
              {["Every lab, every level", "Browser + VPN labs", "Level certificates", "New labs added regularly"].map((f) => (
                <li key={f} className="flex gap-2.5"><CheckCircle2 className={`w-4 h-4 ${CYAN} mt-0.5 shrink-0`} /> {f}</li>
              ))}
            </ul>
            <Link to="/register">
              <Button data-testid="pricing-monthly-cta" className="w-full mt-8 rounded-none bg-[#FF3B30] text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 dark:hover:bg-white dark:hover:text-black">
                Start free, upgrade anytime
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative px-6 py-24 md:py-32 text-center overflow-hidden bg-zinc-50 dark:bg-[#050505]">
        <div className="absolute inset-0 tactical-grid opacity-50" />
        <div className="relative">
          <Award className="w-9 h-9 text-[#FF3B30] mx-auto mb-6" />
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl max-w-3xl mx-auto leading-[0.95] text-zinc-900 dark:text-white">
            The labs are waiting.<br />So are the attackers.
          </h2>
          <p className="mt-6 text-sm font-mono text-zinc-600 dark:text-zinc-400 max-w-xl mx-auto">
            Spin up your first lab in 60 seconds. Start with a free one, on the house.
          </p>
          <Link to="/register">
            <Button data-testid="final-cta-register" size="lg"
              className="mt-9 rounded-none bg-[#FF3B30] text-white font-mono text-sm uppercase tracking-wider px-8 h-12 hover:bg-zinc-900 dark:hover:bg-white dark:hover:text-black active:scale-95 transition-all">
              Create your account <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
